/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {FormsModule} from '@angular/forms';
import * as _ from 'lodash';
import template from './rate-limit.component.html';
import {formatStatCurrency} from '../filters/format-stat-currency';
import {AppLocaleService} from '../locale/app-locale.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {AppSharedModule} from '../app-shared.module';
import {FormControlsModule} from '../directives/form-controls/form-controls.module';

const DEFAULT_ORDER = ['IMPRESSION', 'RENDERED_IMPRESSION', 'VISIBLE_IMPRESSION', 'VIEWABLE_IMPRESSION', 'CLICK', 'CONVERSION', 'BUDGET'];
const PERIOD_TYPES = ['HOURS', 'DAYS', 'WEEKS', 'MONTHS', 'ALL_TIME'];

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, FormControlsModule],
  selector: 'adn-rate-limit',
  template
})
export class RateLimitComponent implements OnChanges {
  @Input() model: any;
  @Input() rateLimitTypes: string[] = [];
  @Input() perUserOnly = false;
  @Input() parentCtrl: any;
  @Input() readOnly = false;
  @Input() hideLabel = false;

  readonly periodTypes = PERIOD_TYPES;

  primaryNetworkCurrency: any;
  hideGlobal = false;
  transSuffix = '';
  rateLimitTypesOrdered: string[] = [];
  fieldNameTranslated = '';

  private editingRateLimits = new WeakSet<any>();

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly translate: TranslateService,
    private readonly appLocale: AppLocaleService
  ) {
    this.primaryNetworkCurrency = LocalNetworkProfile.get('defaultCurrency');
    this.hideGlobal = LocalNetworkProfile.isPureMarketplacePlatform();
    this.transSuffix = LocalNetworkProfile.getDefaults().rateLimitTranslations === '2' ? '2'
      : LocalNetworkProfile.getDefaults().rateLimitTranslations === '3' ? '3' : '';
    this.fieldNameTranslated = this.translate.instant('lineItem.rateLimit.general' + this.transSuffix + '.title');
    this.updateRateLimitTypesOrdered();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rateLimitTypes) {
      this.updateRateLimitTypesOrdered();
    }
    if (changes.model && this.model?.rateLimits) {
      this.model.rateLimits.forEach((rateLimit: any) => {
        if (rateLimit.amount === null) {
          this.expand(rateLimit);
        }
      });
    }
  }

  get rateLimits(): any[] {
    return this.model?.rateLimits || [];
  }

  generalKey(suffix: string): string {
    return 'lineItem.rateLimit.general' + this.transSuffix + suffix;
  }

  scopeKey(scope: string): string {
    return 'lineItem.rateLimit.scope' + this.transSuffix + '.' + scope;
  }

  typeKey(type: string, singular: boolean): string {
    return 'lineItem.rateLimit.type.' + type + (singular ? '.singular' : '');
  }

  periodTypeKey(periodType: string, singular: boolean): string {
    return 'lineItem.rateLimit.periodType.' + periodType + (singular ? '.singular' : '');
  }

  formatStatCurrency(amount: any): string {
    return formatStatCurrency(amount, this.LocalNetworkProfile.get('defaultCurrency'), this.appLocale.getLocaleId());
  }

  isExpanded(rateLimit: any): boolean {
    return this.editingRateLimits.has(rateLimit);
  }

  expand(rateLimit: any): void {
    this.editingRateLimits.add(rateLimit);
  }

  collapse(rateLimit: any): void {
    this.editingRateLimits.delete(rateLimit);
  }

  onFieldChange(): void {
    if (_.isFunction(this.parentCtrl?.editsMade)) {
      this.parentCtrl.editsMade();
    }
  }

  deleteLimit(index: number): void {
    this.onFieldChange();
    if (!this.model.rateLimits) {
      return;
    }
    _.pullAt(this.model.rateLimits, index);
  }

  addLimit(): void {
    this.onFieldChange();
    this.model.rateLimits = this.model.rateLimits || [];
    const rateLimit = {
      scope: this.perUserOnly ? 'PER_USER' : (this.LocalNetworkProfile.getDefaults().rateLimitScope || 'GLOBAL'),
      type: this.rateLimitTypesOrdered[0],
      amount: null,
      periodAmount: 1,
      periodType: 'WEEKS',
      smoothed: !this.perUserOnly
    };
    this.model.rateLimits.unshift(rateLimit);
    this.expand(rateLimit);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private updateRateLimitTypesOrdered(): void {
    this.rateLimitTypesOrdered = _.filter(DEFAULT_ORDER, (rateLimit: string) => {
      return (this.rateLimitTypes || []).indexOf(rateLimit) > -1;
    });
  }
}
